import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, MouseEvent as ReactMouseEvent, ReactNode, Ref } from 'react';
import tinyCookie from 'tiny-cookie';
import {
  BUSINESS_ARROW_URL,
  BUSINESS_MENU_TRIANGLE_URL,
  LOGO_URL,
  PC_VIDEO_URL,
  PROTECTED_DESTINATIONS,
  PUBLIC_WEBSITE_URL,
  RECRUITER_BADGE_URL,
  REFERRAL_CLOSE_URL,
  REFERRAL_IMAGE_URL,
  REFERRAL_URL,
  SEARCH_ICON_URL,
  SERVICE_SEPARATOR_URL,
  TESTIMONIAL_TITLE_URL,
  VIDEO_COPY_IMAGE_URL,
  VIDEO_TRIGGER_IMAGE_URL,
  buildLoginUrl,
  businessMenuItems,
  pcFeatures,
  serviceStripItems,
  testimonials,
} from './content';
import { PERSONAL_SALE_MEMBER_ALERT, resolveSaleMemberAction } from './headerContract';
import {
  trackWebsiteChat,
  trackWebsiteCompanyGuide,
  trackWebsiteNavigationShow,
  trackWebsiteReferralClick,
  trackWebsiteReferralShow,
  trackWebsiteSearch,
} from './tracking';
import type { SaleMemberKind, WebsiteViewer } from './types';
import VideoDialog from './VideoDialog';
import styles from './WebsitePage.module.scss';

interface PcWebsiteProps {
  isLoggedIn: boolean;
  saleMemberKind: SaleMemberKind;
  viewer: WebsiteViewer | null;
}

interface CompanyIntegrationData {
  companyBadge: number | undefined;
  companyIsVip: boolean | undefined;
}

const COMPANY_CLICK_COOKIE = 'company_time';
const COMPANY_GUIDE_COOKIE = 'company_mask_show_time';
const COMPANY_GUIDE_IMAGE_URL = 'https://i9.taou.com/maimai/p/25143/2409_53_21sjr63HGUGZGXzZ';
const COMPANY_GUIDE_CLOSE_URL = 'https://i9.taou.com/maimai/p/25135/7468_53_1baWx1U7frozGJ';
const COMPANY_HOME_URL = 'https://maimai.cn/ent/v2/company/home';
const COMPANY_MARKETING_URL = 'https://maimai.cn/company/marketing?fr=main_index';
const ENTERPRISE_HOME_URL = 'https://maimai.cn/ent';
const ENTERPRISE_V41_HOME_URL = 'https://maimai.cn/ent/v41/loading';
const ENTERPRISE_V41_CHAT_URL = 'https://maimai.cn/ent/v41/im?scene=pc_nav';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function toBadgeCount(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return undefined;
  }

  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? Math.trunc(count) : undefined;
}

function parseChatBadge(messagePayload: unknown): number | undefined {
  return toBadgeCount(asRecord(messagePayload)?.badges_number);
}

function parseCompanyIntegrationData(companyPayload: unknown): CompanyIntegrationData {
  const companyUser = asRecord(asRecord(companyPayload)?.companyUser);
  const companyData = asRecord(companyUser?.data);
  const employeeUnread = toBadgeCount(companyData?.employee_feed_unread_cnt);
  const otherUnread = toBadgeCount(companyData?.other_feed_unread_cnt);
  const companyBadge =
    employeeUnread === undefined && otherUnread === undefined ? undefined : (employeeUnread ?? 0) + (otherUnread ?? 0);
  let companyIsVip: boolean | undefined;
  if (companyData?.is_company_vip === 1 || companyData?.is_company_vip === '1') {
    companyIsVip = true;
  } else if (companyData?.is_company_vip === 0 || companyData?.is_company_vip === '0') {
    companyIsVip = false;
  }

  return {
    companyBadge,
    companyIsVip,
  };
}

async function requestWebsiteJson(path: string): Promise<unknown> {
  const response = await fetch(path, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('platform website integration request failed');
  }

  return response.json();
}

function openNewProcessTab(url: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.click();
}

function reserveNewProcessTab(): Window | null {
  try {
    const reservedTab = window.open('', '_blank');
    if (reservedTab) {
      reservedTab.opener = null;
    }
    return reservedTab;
  } catch {
    return null;
  }
}

function navigateNewProcessTab(url: string, reservedTab: Window | null) {
  if (reservedTab && !reservedTab.closed) {
    reservedTab.location.replace(url);
    return;
  }

  openNewProcessTab(url);
}

function closeReservedTab(reservedTab: Window | null) {
  if (reservedTab && !reservedTab.closed) {
    reservedTab.close();
  }
}

function isSameLegacyWeek(olderTimestamp: number, newerTimestamp: number) {
  const olderDay = Math.trunc(olderTimestamp / ONE_DAY_MS);
  const newerDay = Math.trunc(newerTimestamp / ONE_DAY_MS);
  return Math.trunc((olderDay + 4) / 7) === Math.trunc((newerDay + 4) / 7);
}

function readWeeklyCookieTimestamp(name: string): number | null {
  try {
    const value = tinyCookie.get(name);
    const timestamp = Number(value);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
  } catch {
    return null;
  }
}

function wasCookieSetThisWeek(name: string, now = Date.now()) {
  const timestamp = readWeeklyCookieTimestamp(name);
  return timestamp !== null && isSameLegacyWeek(timestamp, now);
}

function writeWeeklyCookieTimestamp(name: string, timestamp = Date.now()) {
  try {
    tinyCookie.set(name, String(timestamp), { expires: '30D', SameSite: 'Lax' });
  } catch {
    // A disabled cookie jar must not make the public website unusable.
  }
}

function formatBadgeCount(count: number | undefined) {
  if (count === undefined || count <= 0) {
    return null;
  }

  return count > 99 ? '99+' : String(count);
}

function createSearchSid() {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = (Math.random() * 16) | 0;
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function ProtectedHeaderLink({
  anchorRef,
  children,
  className,
  destination,
  isLoggedIn,
  onClick,
  testId,
}: {
  anchorRef?: Ref<HTMLAnchorElement>;
  children: ReactNode;
  className?: string;
  destination: string;
  isLoggedIn: boolean;
  onClick?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
  testId?: string;
}) {
  return (
    <a
      ref={anchorRef}
      className={className}
      href={isLoggedIn ? destination : buildLoginUrl(destination)}
      onClick={isLoggedIn ? onClick : undefined}
      data-testid={testId}
      {...(isLoggedIn ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

function PcHeader({ isLoggedIn, saleMemberKind, viewer }: PcWebsiteProps) {
  const [chatBadge, setChatBadge] = useState<number>();
  const [companyBadge, setCompanyBadge] = useState<number>();
  const [companyBadgeIsSynthetic, setCompanyBadgeIsSynthetic] = useState(false);
  const [companyGuideLeft, setCompanyGuideLeft] = useState(0);
  const [companyGuideVisible, setCompanyGuideVisible] = useState(false);
  const companyLinkRef = useRef<HTMLAnchorElement>(null);
  const entVersionPromiseRef = useRef<Promise<boolean> | null>(null);
  const chatBadgePromiseRef = useRef<Promise<number | undefined> | null>(null);
  const companyIntegrationPromiseRef = useRef<Promise<CompanyIntegrationData> | null>(null);
  const saleReturnAlertShownRef = useRef(false);

  const getEntVersion = useCallback(() => {
    if (!entVersionPromiseRef.current) {
      entVersionPromiseRef.current = requestWebsiteJson('/sdk/jobs/get_ent_version')
        .then((payload) => Boolean(asRecord(payload)?.greaterThanV3))
        .catch(() => false);
    }

    return entVersionPromiseRef.current;
  }, []);

  const getChatBadge = useCallback(() => {
    if (!chatBadgePromiseRef.current) {
      chatBadgePromiseRef.current = requestWebsiteJson('/n/platform/api/website/chat-badge')
        .then(parseChatBadge)
        .catch(() => undefined);
    }

    return chatBadgePromiseRef.current;
  }, []);

  const getCompanyIntegrationData = useCallback(() => {
    if (!companyIntegrationPromiseRef.current) {
      companyIntegrationPromiseRef.current = requestWebsiteJson('/n/platform/api/website/company-admin')
        .then(parseCompanyIntegrationData)
        .catch(
          (): CompanyIntegrationData => ({
            companyBadge: undefined,
            companyIsVip: undefined,
          })
        );
    }

    return companyIntegrationPromiseRef.current;
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }

    void getEntVersion();
    let active = true;
    const chatBadgePromise = getChatBadge();
    const companyIntegrationPromise = getCompanyIntegrationData();

    void chatBadgePromise.then((resolvedChatBadge) => {
      if (active) {
        setChatBadge(resolvedChatBadge);
      }
    });

    void companyIntegrationPromise.then((data) => {
      if (!active) {
        return;
      }

      const now = Date.now();
      const syntheticCompanyBadge =
        data.companyIsVip &&
        (data.companyBadge === 0 || data.companyBadge === undefined) &&
        !wasCookieSetThisWeek(COMPANY_CLICK_COOKIE, now);
      let visibleCompanyBadge: number | undefined;
      if (data.companyIsVip) {
        visibleCompanyBadge = data.companyBadge && data.companyBadge > 0 ? data.companyBadge : undefined;
        if (syntheticCompanyBadge) {
          visibleCompanyBadge = 1;
        }
      }

      setCompanyBadge(visibleCompanyBadge);
      setCompanyBadgeIsSynthetic(syntheticCompanyBadge);

      if (data.companyIsVip && !wasCookieSetThisWeek(COMPANY_GUIDE_COOKIE, now)) {
        const companyLeft = companyLinkRef.current?.getBoundingClientRect().left;
        setCompanyGuideLeft(companyLeft === undefined ? 0 : Math.max(0, companyLeft + window.scrollX - 20));
        writeWeeklyCookieTimestamp(COMPANY_GUIDE_COOKIE, now);
        setCompanyGuideVisible(true);
        trackWebsiteCompanyGuide('show');
      }
    });

    void Promise.all([chatBadgePromise, companyIntegrationPromise]).then(([resolvedChatBadge, data]) => {
      if (active) {
        trackWebsiteNavigationShow(resolvedChatBadge, data.companyIsVip ? data.companyBadge : undefined);
      }
    });

    return () => {
      active = false;
    };
  }, [getChatBadge, getCompanyIntegrationData, getEntVersion, isLoggedIn]);

  useEffect(() => {
    if (!companyGuideVisible) {
      return undefined;
    }

    const alignGuide = () => {
      const companyLeft = companyLinkRef.current?.getBoundingClientRect().left;
      if (companyLeft !== undefined) {
        setCompanyGuideLeft(Math.max(0, companyLeft + window.scrollX - 20));
      }
    };

    window.addEventListener('resize', alignGuide);
    return () => window.removeEventListener('resize', alignGuide);
  }, [companyGuideVisible]);

  useEffect(() => {
    if (!saleReturnAlertShownRef.current && /fr=personal_sale/i.test(window.location.search)) {
      saleReturnAlertShownRef.current = true;
      window.alert(PERSONAL_SALE_MEMBER_ALERT);
    }
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const query = String(formData.get('query') || '').trim();

    if (!query) {
      return;
    }

    const sid = createSearchSid();
    const target = new URL('https://maimai.cn/web/search_center');
    target.searchParams.set('type', 'feed');
    target.searchParams.set('query', query);
    target.searchParams.set('highlight', 'true');
    target.searchParams.set('sid', sid);
    trackWebsiteSearch(query, sid);
    window.location.replace(target.toString());
  };

  const recordCompanyClick = () => {
    writeWeeklyCookieTimestamp(COMPANY_CLICK_COOKIE);
    if (companyBadgeIsSynthetic) {
      setCompanyBadge(undefined);
      setCompanyBadgeIsSynthetic(false);
    }
  };

  const handleRecruiterClick = async (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (!isLoggedIn) {
      return;
    }

    event.preventDefault();
    const greaterThanV3 = await getEntVersion();
    window.location.assign(greaterThanV3 ? ENTERPRISE_V41_HOME_URL : ENTERPRISE_HOME_URL);
  };

  const handleChatClick = async (event: ReactMouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const reservedTab = reserveNewProcessTab();
    trackWebsiteChat(chatBadge);
    const greaterThanV3 = await getEntVersion();
    navigateNewProcessTab(greaterThanV3 ? ENTERPRISE_V41_CHAT_URL : PROTECTED_DESTINATIONS.chat, reservedTab);
  };

  const handleCompanyClick = async (event: ReactMouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const reservedTab = reserveNewProcessTab();
    const data = await getCompanyIntegrationData();
    recordCompanyClick();
    navigateNewProcessTab(data.companyIsVip === true ? COMPANY_HOME_URL : COMPANY_MARKETING_URL, reservedTab);
  };

  const handleMarketingClick = async (event: ReactMouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const reservedTab = reserveNewProcessTab();

    try {
      const payload = asRecord(await requestWebsiteJson('/sdk/marketing_center/marketing/home'));
      if (payload?.error_code === 21003 || payload?.error_code === '21003') {
        closeReservedTab(reservedTab);
        window.alert('非常抱歉您暂时没有开通营销中心的权限，如有需要请联系脉脉客服！');
        return;
      }
      if (payload?.result === 'ok') {
        navigateNewProcessTab(PROTECTED_DESTINATIONS.marketing, reservedTab);
        return;
      }
      closeReservedTab(reservedTab);
    } catch {
      closeReservedTab(reservedTab);
      // Preserve the readable public page when an identity-dependent integration is unavailable.
    }
  };

  const handleSaleMemberClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    const action = resolveSaleMemberAction(saleMemberKind);
    if (action.type === 'generic') {
      return;
    }

    event.preventDefault();
    if (action.type === 'alert') {
      window.alert(PERSONAL_SALE_MEMBER_ALERT);
      return;
    }
    openNewProcessTab(action.url);
  };

  const openCompanyFromGuide = (targetType: 'click_nav' | 'click_tip') => {
    recordCompanyClick();
    trackWebsiteCompanyGuide(targetType);
    setCompanyGuideVisible(false);
    openNewProcessTab(COMPANY_HOME_URL);
  };

  return (
    <header className={styles.navbar} data-testid='pc-header'>
      <div className={styles.headerContainer}>
        <a className={styles.logo} href={PUBLIC_WEBSITE_URL} aria-label='脉脉官网首页'>
          {/* The verified legacy asset is intentionally rendered without Next image rewriting. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={LOGO_URL} alt='脉脉' />
        </a>

        <form className={styles.search} role='search' onSubmit={submitSearch}>
          <label className={styles.srOnly} htmlFor='pc-website-search'>
            搜索脉脉内容
          </label>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className={styles.searchIcon} src={SEARCH_ICON_URL} alt='' aria-hidden='true' />
          <input
            id='pc-website-search'
            data-testid='pc-search-input'
            name='query'
            type='text'
            placeholder='搜索'
            autoComplete='off'
          />
        </form>

        <div className={styles.navbarLinks}>
          <nav className={styles.navLinks} aria-label='官网主导航'>
            <a className={styles.activeNavItem} href={PUBLIC_WEBSITE_URL}>
              首页
            </a>
            <div className={styles.businessServices} data-testid='pc-business-menu'>
              <button type='button' aria-haspopup='true'>
                商业服务
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={BUSINESS_ARROW_URL} className={styles.businessArrow} alt='' aria-hidden='true' />
              </button>
              <div className={styles.businessMenu}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img className={styles.businessTriangle} src={BUSINESS_MENU_TRIANGLE_URL} alt='' aria-hidden='true' />
                {businessMenuItems.map((item) => (
                  <a
                    href={item.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    key={item.title}
                    onClick={'memberAware' in item && item.memberAware ? handleSaleMemberClick : undefined}
                  >
                    <strong>{item.title}</strong>
                    <span>{item.description}</span>
                  </a>
                ))}
              </div>
            </div>
            <a href='https://maimai.cn/community'>社区</a>
            <a
              className={styles.recruiterLink}
              href={isLoggedIn ? ENTERPRISE_HOME_URL : 'https://maimai.cn/ent/index?fr=ent'}
              onClick={handleRecruiterClick}
            >
              招聘管理
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={RECRUITER_BADGE_URL} alt='招人才' />
            </a>
            <ProtectedHeaderLink
              className={styles.headerStatefulLink}
              destination={PROTECTED_DESTINATIONS.chat}
              isLoggedIn={isLoggedIn}
              onClick={handleChatClick}
              testId='pc-chat-link'
            >
              网页聊天
              {formatBadgeCount(chatBadge) && (
                <span className={styles.navBadge} data-testid='pc-chat-badge'>
                  {formatBadgeCount(chatBadge)}
                </span>
              )}
            </ProtectedHeaderLink>
            <ProtectedHeaderLink
              anchorRef={companyLinkRef}
              className={styles.headerStatefulLink}
              destination={PROTECTED_DESTINATIONS.company}
              isLoggedIn={isLoggedIn}
              onClick={handleCompanyClick}
            >
              企业号
              {formatBadgeCount(companyBadge) && (
                <span className={styles.navBadge} data-testid='pc-company-badge'>
                  {formatBadgeCount(companyBadge)}
                </span>
              )}
            </ProtectedHeaderLink>
            <ProtectedHeaderLink
              destination={PROTECTED_DESTINATIONS.marketing}
              isLoggedIn={isLoggedIn}
              onClick={handleMarketingClick}
            >
              品牌号
            </ProtectedHeaderLink>
            <a href='https://maimai.cn/platform/contact_us'>联系我们</a>
          </nav>

          <div className={styles.loginZone}>
            {isLoggedIn ? (
              <div className={styles.userProfile} aria-label='当前登录账号'>
                {viewer?.avatar && (
                  // Mycard avatars may be served by multiple approved CDN hosts.
                  // eslint-disable-next-line @next/next/no-img-element
                  <img className={styles.userAvatar} src={viewer.avatar} alt={`${viewer.name}的头像`} />
                )}
                {viewer ? (
                  <span className={styles.userName} data-testid='viewer-name'>
                    {viewer.name}
                  </span>
                ) : (
                  <span className={styles.userName} data-testid='viewer-unavailable'>
                    已登录
                  </span>
                )}
                <a className={styles.logout} href='/sdk/platform/logout'>
                  退出
                </a>
              </div>
            ) : (
              <a className={styles.loginButton} data-testid='login-cta' href={buildLoginUrl(PUBLIC_WEBSITE_URL)}>
                登录/注册
              </a>
            )}
          </div>
        </div>
      </div>
      {companyGuideVisible && (
        <div className={styles.companyGuideMask} data-testid='pc-company-guide'>
          <button
            className={styles.companyGuideNav}
            type='button'
            style={{ left: companyGuideLeft }}
            onClick={() => openCompanyFromGuide('click_nav')}
          >
            <span className={styles.companyGuideCopy}>
              企业号
              {formatBadgeCount(companyBadge) && (
                <span className={styles.navBadge}>{formatBadgeCount(companyBadge)}</span>
              )}
            </span>
          </button>
          <div className={styles.companyGuideTip} style={{ left: companyGuideLeft }}>
            <button
              className={styles.companyGuideImage}
              type='button'
              aria-label='进入企业号'
              style={{ backgroundImage: `url(${COMPANY_GUIDE_IMAGE_URL})` }}
              onClick={() => openCompanyFromGuide('click_tip')}
            />
            <button
              className={styles.companyGuideClose}
              type='button'
              aria-label='关闭企业号引导'
              style={{ backgroundImage: `url(${COMPANY_GUIDE_CLOSE_URL})` }}
              onClick={() => {
                trackWebsiteCompanyGuide('close');
                setCompanyGuideVisible(false);
              }}
            />
          </div>
        </div>
      )}
    </header>
  );
}

function ServiceAndVideo({ onVideoOpen }: { onVideoOpen: () => void }) {
  return (
    <section className={styles.serviceVideoSection} data-testid='pc-service-strip' aria-label='脉脉商业服务入口'>
      <div className={styles.serviceStrip}>
        {serviceStripItems.map((item, index) => (
          <span className={styles.serviceItemGroup} key={item.position}>
            <a href={item.href} target='_blank' rel='noopener noreferrer'>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img className={styles[`service_${item.position}`]} src={item.src} alt={item.alt} />
            </a>
            {index < serviceStripItems.length - 1 && (
              // eslint-disable-next-line @next/next/no-img-element
              <img className={styles.serviceSeparator} src={SERVICE_SEPARATOR_URL} alt='' aria-hidden='true' />
            )}
          </span>
        ))}
      </div>
      <div className={styles.contentInner}>
        <div className={`${styles.videoColumn} ${styles.revealLeft}`} data-reveal='true'>
          <button
            className={styles.videoTrigger}
            data-testid='pc-video-trigger'
            type='button'
            onClick={onVideoOpen}
            aria-label='播放脉脉介绍视频'
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={VIDEO_TRIGGER_IMAGE_URL} alt='播放脉脉介绍视频' />
          </button>
        </div>
        <div className={`${styles.videoColumn} ${styles.revealRight}`} data-reveal='true'>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className={styles.videoCopy} src={VIDEO_COPY_IMAGE_URL} alt='中高端人才阵地：段位高、密度高、活跃度高' />
        </div>
      </div>
    </section>
  );
}

function PcFeatureSections() {
  return (
    <>
      {pcFeatures.map((feature) => (
        <section
          className={`${styles.featureSection} ${feature.id % 2 === 1 ? styles.featureSectionMuted : ''}`}
          data-testid={`pc-feature-${feature.id}`}
          key={feature.id}
        >
          <div className={styles.contentInner}>
            <div className={`${styles.featureColumn} ${styles.revealLeft}`} data-reveal='true'>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={`${styles.featureImage} ${styles[`feature${feature.id}Left`]}`}
                src={feature.left.src}
                alt={feature.left.alt}
              />
            </div>
            <div className={`${styles.featureColumn} ${styles.revealRight}`} data-reveal='true'>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={`${styles.featureImage} ${styles[`feature${feature.id}Right`]}`}
                src={feature.right.src}
                alt={feature.right.alt}
              />
            </div>
          </div>
        </section>
      ))}
    </>
  );
}

function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % testimonials.length);
    }, 4000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <section className={styles.testimonials} data-testid='pc-testimonials' aria-label='脉脉用户评价'>
      <div className={styles.testimonialContainer}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className={styles.testimonialTitle} src={TESTIMONIAL_TITLE_URL} alt='1.2亿职场人都在用' />
        <div className={styles.testimonialSlider}>
          {testimonials.map((testimonial, index) => (
            <article
              className={styles.testimonialSlide}
              aria-hidden={activeIndex !== index}
              key={testimonial.author}
              style={{ transform: `translateX(${(index - activeIndex) * 100}%)` }}
            >
              <div className={styles.testimonialCard}>
                <div className={styles.testimonialAvatar}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={testimonial.avatar} alt={`${testimonial.author}的头像`} />
                </div>
                <div className={styles.testimonialMain}>
                  <p>{testimonial.quote}</p>
                  <p className={styles.testimonialCopyright}>
                    <strong>@{testimonial.author}</strong>
                    {testimonial.role}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className={styles.dataStatement}>* 画面数据来源于截至2020年08月28日脉脉内部数据统计</p>
      </div>
    </section>
  );
}

function PcFooter() {
  return (
    <footer className={styles.footer} data-testid='pc-footer'>
      <div className={styles.footerContainer}>
        <nav className={styles.minorLinks} aria-label='官网页脚导航'>
          <a href='https://maimai.cn/platform/about_us'>关于脉脉</a>
          <a href='https://maimai.jobs.feishu.cn/index' target='_blank' rel='noopener noreferrer'>
            脉脉社招
          </a>
          <a href='https://maimai.jobs.feishu.cn/campus' target='_blank' rel='noopener noreferrer'>
            脉脉实习
          </a>
        </nav>
        <div className={styles.websiteInfo}>
          <div className={styles.websiteCopyright}>
            <p>违法不良信息&amp;未成年人有害信息举报电话/客服电话/投诉电话：400 065 0808（周一到周五，9:00-18:00）</p>
            <p>
              违法不良信息&amp;未成年人有害信息举报邮箱/客服邮箱/投诉邮箱：
              <a href='mailto:maimai@taou.com'>maimai@taou.com</a>（周一到周日，0:00-24:00）
              <a href='https://maimai.cn/n/platform/rich-text/complaints_report'>脉脉举报投诉流程指引</a>
            </p>
            <p>
              个人/企业等被诽谤侮辱、人身权或知识产权等被侵犯、网络谣言的举报地址：
              <a href='https://maimai.cn/tousu'>maimai.cn/tousu</a>（周一到周日，0:00-24:00）
              <a href='https://maimai.cn/n/platform/report-complaint-rules'>涉企虚假不实信息举报投诉专区</a>
            </p>
            <p>公司全称：北京淘友天下科技发展有限公司</p>
            <p>
              Copyright © maimai.cn. All rights reserved. <a href='https://beian.miit.gov.cn/#'>京ICP备12005786号-1</a>{' '}
              | {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src='https://i9.taou.com/beian/beian.png' height='12' width='12' alt='' aria-hidden='true' />
              京公网安备11010802020015号 |{' '}
              <a href='https://maimai.cn/platform/license_center' target='view_window' className={styles.license}>
                证照中心
              </a>
            </p>
          </div>
          <div className={styles.websiteAuthgov}>
            <a href='https://www.12377.cn/' target='_blank' rel='noopener noreferrer'>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src='https://i9.taou.com/maimai/p/23722/377_53_j1CjU6SHVU3Z9X' alt='中央网信办举报中心' />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PcWebsite({ isLoggedIn, saleMemberKind, viewer }: PcWebsiteProps) {
  const [videoOpen, setVideoOpen] = useState(false);
  const [referralVisible, setReferralVisible] = useState(true);
  const referralTracked = useRef(false);
  const closeVideo = useCallback(() => setVideoOpen(false), []);

  useEffect(() => {
    const revealElements = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal="true"]'));
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      revealElements.forEach((element) => element.setAttribute('data-in-view', 'true'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).setAttribute('data-in-view', 'true');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '100px 0px -100px', threshold: 0 }
    );

    revealElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isLoggedIn && !referralTracked.current) {
      referralTracked.current = true;
      trackWebsiteReferralShow();
    }
  }, [isLoggedIn]);

  return (
    <div className={styles.pcRoot}>
      <PcHeader isLoggedIn={isLoggedIn} saleMemberKind={saleMemberKind} viewer={viewer} />
      <main>
        <section className={styles.hero} data-testid='pc-hero' aria-label='1.2亿职场人都在用的职场社区和求职平台'>
          <div className={styles.contentInner}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src='https://i9.taou.com/maimai/p/36629/8007_6_21TYlXVpv5SH8sH0'
              alt='1.2亿职场人都在用的职场社区和求职平台'
            />
          </div>
        </section>
        <ServiceAndVideo onVideoOpen={() => setVideoOpen(true)} />
        <PcFeatureSections />
        <Testimonials />
      </main>
      <PcFooter />

      {referralVisible && (
        <aside className={styles.referral} aria-label='内推奖励入口'>
          <a
            className={styles.referralLink}
            data-testid='pc-referral-entry'
            href={REFERRAL_URL}
            target='_blank'
            rel='noopener noreferrer'
            onClick={isLoggedIn ? trackWebsiteReferralClick : undefined}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={REFERRAL_IMAGE_URL} alt='内推奖励入口' />
          </a>
          <button
            className={styles.referralClose}
            data-testid='pc-referral-close'
            type='button'
            aria-label='关闭入口'
            onClick={() => setReferralVisible(false)}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={REFERRAL_CLOSE_URL} alt='' aria-hidden='true' />
          </button>
        </aside>
      )}

      <VideoDialog open={videoOpen} source={PC_VIDEO_URL} onClose={closeVideo} />
    </div>
  );
}
